import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, Upload, Users, Image, Settings, FileText, Bell,
  ChevronRight, ChevronLeft, Sparkles, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles, description: 'Get started with your program' },
  { id: 'org_info', title: 'Organization', icon: Building2, description: 'Update your org details' },
  { id: 'upload_staff', title: 'Add Staff', icon: Users, description: 'Add your team members' },
  { id: 'upload_youth', title: 'Add Participants', icon: Upload, description: 'Upload your youth roster' },
  { id: 'customize', title: 'Customize', icon: Settings, description: 'Personalize your program' },
  { id: 'launch', title: 'Launch', icon: CheckCircle, description: 'You\'re ready to go!' },
];

const ClientOnboardingWizard = ({ user, onComplete }) => {
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [orgInfo, setOrgInfo] = useState({ description: '', phone: '', website: '', address: '' });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check what's already done
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/admin/users?limit=5`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const users = data.users || [];
          const hasStaff = users.some(u => u.role === 'lead_monitor' || u.role === 'coach');
          const hasYouth = users.some(u => u.role === 'youth');
          setCompleted(prev => ({
            ...prev,
            org_info: true, // They logged in, so org exists
            upload_staff: hasStaff,
            upload_youth: hasYouth,
          }));
          // If youth exist, they've already done the basics — skip to end
          if (hasYouth && hasStaff) {
            setDismissed(true);
          }
        }
      } catch (e) { console.error(e); }
    };
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check localStorage for dismissed state
  useEffect(() => {
    if (localStorage.getItem('onboarding_dismissed') === 'true') {
      setDismissed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissWizard = () => {
    localStorage.setItem('onboarding_dismissed', 'true');
    setDismissed(true);
    if (onComplete) onComplete();
  };

  const markStep = (stepId) => {
    setCompleted(prev => ({ ...prev, [stepId]: true }));
  };

  const completedCount = Object.values(completed).filter(Boolean).length;
  const progressPct = Math.round((completedCount / (STEPS.length - 2)) * 100); // Exclude welcome and launch

  if (dismissed) return null;

  const currentStep = STEPS[step];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <Card className="rounded-2xl overflow-hidden border-2 border-orange-200" data-testid="onboarding-wizard">
        {/* Progress Header */}
        <div style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #1E6B7B 100%)' }} className="p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold">Program Setup</h2>
              <p className="text-white/70 text-sm">{completedCount} of {STEPS.length - 2} steps completed</p>
            </div>
            <Button onClick={dismissWizard} variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 text-xs">
              Skip for now
            </Button>
          </div>
          <Progress value={progressPct} className="h-2 bg-white/20" />
          
          {/* Step indicators */}
          <div className="flex gap-1 mt-3">
            {STEPS.map((s, i) => (
              <button 
                key={s.id}
                onClick={() => setStep(i)}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  i === step ? 'bg-orange-400' : 
                  completed[s.id] ? 'bg-green-400' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Welcome */}
              {currentStep.id === 'welcome' && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to YAS Connect!</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Let's get your program set up. This wizard will walk you through the essential steps to launch your program.
                  </p>
                  <Button onClick={() => setStep(1)} className="bg-orange-500 hover:bg-orange-600 rounded-xl px-8">
                    Let's Get Started <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Organization Info */}
              {currentStep.id === 'org_info' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Organization Details</h3>
                  <p className="text-sm text-gray-500 mb-4">Update your organization's information and logo</p>
                  <div className="space-y-3">
                    <div><Label className="text-gray-700">Description</Label>
                      <Textarea value={orgInfo.description} onChange={e => setOrgInfo({...orgInfo, description: e.target.value})} placeholder="Brief description of your organization" className="mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-gray-700">Phone</Label>
                        <Input value={orgInfo.phone} onChange={e => setOrgInfo({...orgInfo, phone: e.target.value})} placeholder="(555) 123-4567" className="mt-1" /></div>
                      <div><Label className="text-gray-700">Website</Label>
                        <Input value={orgInfo.website} onChange={e => setOrgInfo({...orgInfo, website: e.target.value})} placeholder="https://..." className="mt-1" /></div>
                    </div>
                    <div><Label className="text-gray-700">Address</Label>
                      <Input value={orgInfo.address} onChange={e => setOrgInfo({...orgInfo, address: e.target.value})} placeholder="Street address, city, state" className="mt-1" /></div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button onClick={() => setStep(0)} variant="outline" className="rounded-xl"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => { markStep('org_info'); setStep(2); toast.success('Organization info saved!'); }} className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl">
                      Continue <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Upload Staff */}
              {currentStep.id === 'upload_staff' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Add Your Staff</h3>
                  <p className="text-sm text-gray-500 mb-4">Add lead monitors, counselors, and other staff members</p>
                  <div className="space-y-3">
                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                      <p className="text-sm text-blue-800 font-semibold mb-1">CSV Upload (Recommended)</p>
                      <p className="text-xs text-blue-600 mb-3">Upload a CSV file with columns: name, email, role (lead_monitor or coach), phone</p>
                      <Button onClick={() => { window.location.href = '/admin'; toast.info('Go to Users tab to upload CSV'); }} variant="outline" className="text-blue-700 border-blue-300 rounded-xl text-xs">
                        <Upload className="w-3 h-3 mr-1" /> Go to Upload Page
                      </Button>
                    </div>
                    <div className="p-4 rounded-xl bg-gray-50">
                      <p className="text-sm text-gray-700 font-semibold">Or invite individually from the Users tab in your admin dashboard</p>
                    </div>
                  </div>
                  {completed.upload_staff && (
                    <div className="mt-3 p-3 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-700 font-semibold">Staff members detected!</p>
                    </div>
                  )}
                  <div className="flex gap-3 mt-6">
                    <Button onClick={() => setStep(1)} variant="outline" className="rounded-xl"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => { markStep('upload_staff'); setStep(3); }} className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl">
                      {completed.upload_staff ? 'Continue' : 'I\'ll do this later'} <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Upload Youth */}
              {currentStep.id === 'upload_youth' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Add Your Participants</h3>
                  <p className="text-sm text-gray-500 mb-4">Upload your youth/student roster to get them on the platform</p>
                  <div className="space-y-3">
                    <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                      <p className="text-sm text-green-800 font-semibold mb-1">CSV Upload (Recommended)</p>
                      <p className="text-xs text-green-600 mb-2">CSV columns: name, email, role (youth), work_site, lead_monitor_email, parent_email, parent_name, program_type, school, grade</p>
                      <Button onClick={() => { window.location.href = '/admin'; toast.info('Go to Users tab to upload CSV'); }} variant="outline" className="text-green-700 border-green-300 rounded-xl text-xs">
                        <Upload className="w-3 h-3 mr-1" /> Go to Upload Page
                      </Button>
                    </div>
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                      <p className="text-xs text-amber-700">When you upload youth, the platform will automatically:</p>
                      <ul className="text-xs text-amber-600 mt-1 space-y-1 ml-4 list-disc">
                        <li>Create accounts for each youth with temporary passwords</li>
                        <li>Send welcome emails with login credentials</li>
                        <li>Create parent accounts if parent_email is provided</li>
                        <li>Assign youth to lead monitors if lead_monitor_email is provided</li>
                      </ul>
                    </div>
                  </div>
                  {completed.upload_youth && (
                    <div className="mt-3 p-3 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-700 font-semibold">Youth participants detected!</p>
                    </div>
                  )}
                  <div className="flex gap-3 mt-6">
                    <Button onClick={() => setStep(2)} variant="outline" className="rounded-xl"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => { markStep('upload_youth'); setStep(4); }} className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl">
                      {completed.upload_youth ? 'Continue' : 'I\'ll do this later'} <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Customize */}
              {currentStep.id === 'customize' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Customize Your Program</h3>
                  <p className="text-sm text-gray-500 mb-4">Set up FAQ, resources, and announcements for your team</p>
                  <div className="space-y-3">
                    {[
                      { label: 'Add FAQ entries', desc: 'Common questions for your youth', tab: 'faq', icon: '❓' },
                      { label: 'Upload resources', desc: 'Documents, guides, handbooks', tab: 'resources', icon: '📁' },
                      { label: 'Create first announcement', desc: 'Welcome message for your cohort', tab: 'announcements', icon: '📢' },
                    ].map((item, i) => (
                      <button key={i} onClick={() => { window.location.href = '/admin'; toast.info(`Go to ${item.tab} tab`); }}
                        className="w-full p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all text-left flex items-center gap-3">
                        <span className="text-2xl">{item.icon}</span>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900">{item.label}</p>
                          <p className="text-xs text-gray-500">{item.desc}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button onClick={() => setStep(3)} variant="outline" className="rounded-xl"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => { markStep('customize'); setStep(5); }} className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl">
                      Continue <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Launch */}
              {currentStep.id === 'launch' && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">You're All Set!</h3>
                  <p className="text-gray-600 mb-2 max-w-md mx-auto">
                    Your program is ready to launch. You can always come back to adjust settings from your admin dashboard.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    {completedCount} of {STEPS.length - 2} setup steps completed
                  </p>
                  <Button onClick={dismissWizard} className="bg-green-600 hover:bg-green-700 rounded-xl px-8">
                    Go to Dashboard <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
};

export default ClientOnboardingWizard;
